import { FormatQuote, LinkedIn } from '@mui/icons-material';
import {
  Box,
  Card,
  Container,
  Fade,
  IconButton,
  Slide,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import me from '../../constants/me';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'transparent',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, rgba(0, 173, 181, 0.05) 0%, transparent 50%),
                 radial-gradient(circle at 80% 80%, rgba(238, 238, 238, 0.02) 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  position: 'relative',
  display: 'block',
  width: '100%',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    transition: 'width 0.8s ease-in-out',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'rgba(0, 173, 181, 0.1)',
    borderRadius: 2,
  },
  '&.animate::after': {
    width: 80,
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  maxWidth: 600,
  margin: `${theme.spacing(6)}px auto ${theme.spacing(8)}px auto`,
  fontStyle: 'italic',
  position: 'relative',
  lineHeight: 1.6,
  paddingTop: theme.spacing(2),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  background: 'rgba(57, 62, 70, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 173, 181, 0.2)',
  borderRadius: theme.spacing(2),
  padding: 0,
  width: '100%',
  maxWidth: 950,
  height: 380,
  margin: '0 auto 2rem auto',
  position: 'relative',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    maxWidth: '100%',
    height: 550,
    margin: '0 auto 1.5rem auto',
  },
  [theme.breakpoints.down('sm')]: {
    height: 500,
    margin: '0 auto 1rem auto',
  },
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(0, 173, 181, 0.4)',
    boxShadow: 'none',
  },
}));

const TestimonialImageSection = styled(Box)(({ theme }) => ({
  flex: '0 0 280px',
  background: 'rgba(34, 40, 49, 0.9)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  position: 'relative',
  borderRight: '1px solid rgba(0, 173, 181, 0.2)',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 180px',
    borderRight: 'none',
    borderBottom: '1px solid rgba(0, 173, 181, 0.2)',
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 160px',
    padding: theme.spacing(2),
  },
}));

const TestimonialPhoto = styled(Box)(({ theme }) => ({
  width: 140,
  height: 140,
  borderRadius: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '3rem',
  fontWeight: 'bold',
  color: '#ffffff',
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: '0 6px 15px rgba(0, 173, 181, 0.3)',
  [theme.breakpoints.down('md')]: {
    width: 100,
    height: 100,
    fontSize: '2rem',
    marginBottom: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    width: 80,
    height: 80,
    fontSize: '1.8rem',
    marginBottom: theme.spacing(1),
  },
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 10px 25px rgba(0, 173, 181, 0.5)',
  },
}));

const AuthorName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.1rem',
    marginBottom: theme.spacing(0.25),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(0.25),
  },
}));

const TestimonialContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4, 4, 3, 4),
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, rgba(0, 173, 181, 0.02) 0%, rgba(0, 173, 181, 0.01) 100%)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 3, 2, 3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2, 2, 2),
  },
}));

const QuoteIcon = styled(FormatQuote)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  fontSize: '4rem',
  color: theme.palette.primary.main,
  opacity: 0.1,
  transform: 'rotate(180deg)',
}));

const TestimonialHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid rgba(0, 173, 181, 0.1)`,
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.5),
  },
}));

const TestimonialTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
    textAlign: 'center',
    marginBottom: theme.spacing(0.25),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    marginBottom: theme.spacing(0.25),
  },
}));

const TestimonialMeta = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1rem',
  opacity: 0.8,
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
  },
}));

const TestimonialText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1rem',
  lineHeight: 1.7,
  fontStyle: 'italic',
  marginBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 2,
  flex: 1,
  overflowY: 'auto',
  paddingRight: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  fontWeight: 400,
  letterSpacing: '0.2px',
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: 3,
    '&:hover': {
      background: theme.palette.primary.light,
    },
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.95rem',
    textAlign: 'center',
    paddingRight: theme.spacing(0.5),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
  },
}));



const LinkedInButton = styled(IconButton)(({ theme }) => ({
  color: '#0077b5',
  background: 'rgba(0, 119, 181, 0.1)',
  border: '1px solid rgba(0, 119, 181, 0.3)',
  transition: 'all 0.3s ease',
  marginTop: theme.spacing(2),
  '&:hover': {
    background: '#0077b5',
    color: theme.palette.background.default,
    transform: 'scale(1.1)',
  },
}));

const SwiperContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  '& .swiper': {
    paddingBottom: theme.spacing(6),
    paddingTop: theme.spacing(2),
    width: '100%',
    overflow: 'hidden',
  },
  '& .swiper-wrapper': {
    alignItems: 'center',
    display: 'flex',
  },
  '& .swiper-slide': {
    display: 'flex !important',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100% !important',
    flexShrink: 0,
  },
  '& .swiper-pagination': {
    bottom: 0,
    '& .swiper-pagination-bullet': {
      background: theme.palette.primary.main,
      opacity: 0.4,
      width: 12,
      height: 12,
      margin: '0 6px',
      '&.swiper-pagination-bullet-active': {
        opacity: 1,
        transform: 'scale(1.3)',
        background: theme.palette.primary.main,
      },
    },
  },
  '& .swiper-button-next, & .swiper-button-prev': {
    color: '#ffffff',
    background: 'linear-gradient(135deg, rgba(0, 173, 181, 0.9), rgba(0, 173, 181, 0.7))',
    borderRadius: '50%',
    width: 45,
    height: 45,
    marginTop: '-22.5px',
    border: '2px solid rgba(0, 173, 181, 0.8)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 15px rgba(0, 173, 181, 0.3)',
    backdropFilter: 'blur(10px)',
    zIndex: 10,
    '&:hover': {
      background: 'linear-gradient(135deg, rgba(0, 173, 181, 1), rgba(0, 173, 181, 0.9))',
      transform: 'scale(1.15)',
      boxShadow: '0 6px 20px rgba(0, 173, 181, 0.5)',
      border: '2px solid rgba(0, 173, 181, 1)',
    },
    '&:after': {
      fontSize: '1.1rem',
      fontWeight: 'bold',
      color: '#ffffff',
    },
    [theme.breakpoints.down('md')]: {
      width: 40,
      height: 40,
      marginTop: '-20px',
      '&:after': {
        fontSize: '1rem',
      },
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  '& .swiper-button-prev': {
    left: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      left: theme.spacing(0.5),
    },
    [theme.breakpoints.down('sm')]: {
      left: theme.spacing(0.25),
    },
  },
  '& .swiper-button-next': {
    right: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      right: theme.spacing(0.5),
    },
    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(0.25),
    },
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  background: 'rgba(34, 40, 49, 0.7)',
  backdropFilter: 'blur(15px)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4, 3, 5, 3),
  border: '1px solid rgba(0, 173, 181, 0.1)',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 2, 4, 2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2, 3, 2),
  },
}));

const TestimonialsSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animateTitle, setAnimateTitle] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setAnimateTitle(true), 300);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('testimonials');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const handleLinkedInClick = (url: string) => {
    window.open(url, '_blank');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  };

  return (
    <SectionContainer id="testimonials">
      <Container maxWidth="lg">
        <ContentWrapper>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <SectionTitle variant="h2" className={animateTitle ? 'animate' : ''}>
                  What People Say
                </SectionTitle>
                <SectionSubtitle variant="subtitle1" sx={{whiteSpace: 'nowrap', textAlign: 'center',margin:'0 auto'}}>
                  "Testimonials and LinkedIn recommendations from colleagues and clients"
                </SectionSubtitle>
              </Box>
            </Fade>
          </Box>

          <Slide direction="up" in={isVisible} timeout={1000}>
            <SwiperContainer>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                slidesPerGroup={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{
                  delay: 7000,
                  disableOnInteraction: false,
                }}
                loop={true}
                speed={800}
                centeredSlides={true}
                watchSlidesProgress={true}
                breakpoints={{
                  0: { slidesPerView: 1, spaceBetween: 0 },
                  768: { slidesPerView: 1, spaceBetween: 0 },
                  1024: { slidesPerView: 1, spaceBetween: 0 },
                  1200: { slidesPerView: 1, spaceBetween: 0 },
                }}
              >
                {me.testimonials.map((testimonial) => (
                  <SwiperSlide key={testimonial.id}>
                    <TestimonialCard>
                      <TestimonialImageSection>
                        <TestimonialPhoto
                          onClick={() => handleLinkedInClick(testimonial.linkedInUrl)}
                        >
                          {getInitials(testimonial.name)}
                        </TestimonialPhoto>
                        
                        <AuthorName variant="h6">
                          {testimonial.name}
                        </AuthorName>
                        
                        <LinkedInButton
                          size="small"
                          onClick={() => handleLinkedInClick(testimonial.linkedInUrl)}
                          aria-label={`View ${testimonial.name}'s LinkedIn profile`}
                        >
                          <LinkedIn />
                        </LinkedInButton>
                      </TestimonialImageSection>

                      <TestimonialContent>
                        <QuoteIcon />
                        
                        <TestimonialHeader>
                          <TestimonialTitle variant="h4">
                            Professional Recommendation
                          </TestimonialTitle>
                          <TestimonialMeta variant="body1">
                            via LinkedIn - {formatDate(testimonial.date)}
                          </TestimonialMeta>
                        </TestimonialHeader>

                        <TestimonialText variant="body1">
                          {testimonial.text}
                        </TestimonialText>
                      </TestimonialContent>
                    </TestimonialCard>
                  </SwiperSlide>
                ))}
              </Swiper>
            </SwiperContainer>
          </Slide>
        </ContentWrapper>
      </Container>
    </SectionContainer>
  );
};

export default TestimonialsSection; 